import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { bucketTopN, toShareRows } from "./_shared/bucketTopN";
import { isValidCutGrade } from "./_shared/isValidCutGrade";
import { barOptions, barPctOptions } from "./_shared/chartTheme";
import ChartState from "./_shared/ChartState";

const DiamondReportsCutGradeComp = ({ data, loading, onSliceClick, onHideNoiseChange, percentOnly }) => {
  const { t } = useTranslation();
  const [hideNoise, setHideNoise] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onHideNoiseChange?.(hideNoise); }, [hideNoise]);
  const othersLabel = t("Others");
  const list = data || [];
  const chartRef = useRef();

  const filteredList = useMemo(
    () => (hideNoise ? list.filter((item) => isValidCutGrade(item.CutGrade)) : list),
    [list, hideNoise]
  );
  const hiddenCount = list.length - filteredList.length;

  const { chartData: bucketed, colors } = bucketTopN(filteredList, {
    labelKey: "CutGrade",
    valueKey: "CNT",
    chartLabel: "Cut Grade",
    max: 10,
    othersLabel,
  });
  const rows = bucketed.slice(1);

  const handleClick = (event) => {
    if (!onSliceClick || !chartRef.current) return;
    const [el] = getElementAtEvent(chartRef.current, event);
    if (!el) return;
    const rawLabel = rows[el.index]?.[0];
    if (rawLabel && !rawLabel.startsWith(`${othersLabel} (`)) {
      onSliceClick(rawLabel.replace(/\s*\([^)]*\)\s*$/, ""));
    }
  };

  const chartJsData = {
    labels: rows.map((row) => row[0]),
    datasets: [
      {
        label: t("Count"),
        // percentOnly (Global Diamond, Task 2344): plot shares, not raw counts.
        data: (percentOnly ? toShareRows(rows) : rows).map((row) => row[1]),
        backgroundColor: rows.map((row, i) =>
          row[0].startsWith(`${othersLabel} (`) ? "#CCCCCC" : colors[i]
        ),
        borderRadius: 4,
      },
    ],
  };

  return (
    <React.Fragment>
      <div className="edr-panel mt-4">
        <div className="edr-panel-head">
          <label>
            <b> {t("Diamond CutGrade Searches")} </b>
          </label>
          <div className="edr-toggle">
            <span>{t("Hide noise / invalid terms")}</span>
            <label className="edr-switch">
              <input
                type="checkbox"
                checked={hideNoise}
                onChange={(e) => setHideNoise(e.target.checked)}
              />
              <span className="edr-switch-track"></span>
            </label>
            {/* hidden-terms count is a raw number - not shown on percent-only reports */}
            {!percentOnly && hiddenCount > 0 && (
              <span className="edr-badge edr-flag">
                {hiddenCount} {t("junk terms hidden")}
              </span>
            )}
          </div>
        </div>
        <div className="chart__section">
          {!loading && rows.length > 0 ? (
            <div className="edr-chartwrap edr-tall">
              <Bar ref={chartRef} data={chartJsData} options={percentOnly ? barPctOptions(true) : barOptions(true)} onClick={handleClick} />
            </div>
          ) : (
            <ChartState loading={loading} />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default DiamondReportsCutGradeComp;
